import { RARITY_THEME, type Rarity, rarityColorVariable } from '@kenocyber/shared';

/**
 * Rarity presentation, derived from `RARITY_THEME` and nothing else.
 *
 * There is deliberately no second colour table here. The ten colours live in
 * `@kenocyber/shared` and are mirrored once into CSS custom properties
 * (`styles/tokens.css`); this module only decides which properties a cell
 * should read, so a rarity can never be painted a colour the catalog does not
 * know about.
 */

/** Inline custom properties a rarity-bordered element needs. */
export function rarityStyle(rarity: Rarity): Record<string, string> {
  const theme = RARITY_THEME[rarity];
  const style: Record<string, string> = {
    // Always present, whatever the paint kind: the single colour every state
    // (equipped, locked, selected, hovered, reduced-motion) falls back to.
    '--cell-rarity': `var(${rarityColorVariable(rarity, 0)})`,
    '--cell-rarity-intensity': String(theme.intensity),
  };
  // Anomaly blends two colours; Prismatic cycles the full spectrum. Both are
  // expressed as extra stops so the CSS can build a gradient without knowing
  // any specific rarity's name.
  theme.colors.forEach((_, index) => {
    style[`--cell-rarity-${index}`] = `var(${rarityColorVariable(rarity, index)})`;
  });
  style['--cell-rarity-stops'] = theme.colors
    .map((_, index) => `var(${rarityColorVariable(rarity, index)})`)
    .join(', ');
  return style;
}

/** `solid` | `dual` | `spectrum`, straight from the catalog. */
export function rarityPaint(rarity: Rarity): string {
  return RARITY_THEME[rarity].paint;
}

export function rarityLabel(rarity: Rarity): string {
  return RARITY_THEME[rarity].label;
}

/** Highest tier first, for the default inventory ordering. */
export function rarityTierOf(rarity: Rarity): number {
  return RARITY_THEME[rarity].tier;
}

export const RARITY_ORDER: readonly Rarity[] = Object.values(RARITY_THEME)
  .slice()
  .sort((a, b) => a.tier - b.tier)
  .map((theme) => theme.id);
